import useBrandSelect, { BrandOption, mapBrandToOption } from 'Modules/Plans/Brand/hooks/useBrandSelect';
import { Form, useFormikContext } from 'formik';
import { FormField, SelectField } from '@round/whitelabel-ui';
import omit from 'lodash/omit';
import { useState } from 'react';
import CreateBrandModal from 'Modules/Plans/Brand/CreateBrandModal/CreateBrandModal';
import { creatorbase } from '@round/api';
import styles from './ProjectForm.module.css';
import { Button } from '@round/whitelabel-ui';
import BrandBadge from 'Modules/Plans/Brand/BrandBadge/BrandBadge';
import { FormatOptionLabelMeta, ValueType } from 'react-select';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { ImageUploadField } from '@round/whitelabel-ui';
import { SUPPORTED_IMAGE_EXTENSIONS } from 'utility/constants';
import { network } from '@round/utils';

export type ProjectFormValues = Pick<creatorbase.PostProjectData, 'name'> & {
    brand: BrandOption | null;
    image: File | null;
};

const ProjectForm = () => {
    const [isCreateBrandModalOpen, setIsCreateBrandModalOpen] = useState(false);

    const formikContext = useFormikContext<ProjectFormValues>();
    const setFallbackProjectImage = async (image: File | string | null | undefined) => {
        if (formikContext.touched.image) {
            return;
        }

        if (!image || image instanceof File) {
            formikContext.setFieldValue('image', image ?? null);
            return;
        }

        try {
            // Setting form submitting as a hack to disable form submission
            // before the image is downloaded and set.
            // Form fields are also disabled to not allow editing during that time
            formikContext.setSubmitting(true);
            const file = await network.fetchFile(image);
            if (file instanceof File) {
                formikContext.setFieldValue('image', file);
                return;
            }

            formikContext.setFieldValue('image', null);
        } catch {
            formikContext.setFieldValue('image', null);
        } finally {
            formikContext.setSubmitting(false);
        }
    };

    const handleBrandCreated = async (brand: creatorbase.Brand) => {
        formikContext.setFieldValue('brand', mapBrandToOption(brand));
        await setFallbackProjectImage(brand.image);
    };

    const { selectedClientId } = useClientSelect();

    const { getProps: getBrandSelectProps } = useBrandSelect({
        initOn: 'menuOpen',
        params: { client_id: selectedClientId },
        initialValueId: formikContext.values.brand?.value,
    });

    return (
        <>
            <Form className={styles.container}>
                <ImageUploadField
                    name="image"
                    options={{ accept: SUPPORTED_IMAGE_EXTENSIONS }}
                    disabled={formikContext.isSubmitting}
                />

                <FormField
                    type="text"
                    name="name"
                    label="Name"
                    placeholder="Enter project name"
                    className={styles.field}
                    disabled={formikContext.isSubmitting}
                />

                <div className={styles.brandField}>
                    <SelectField
                        name="brand"
                        label="Artist"
                        {...omit(getBrandSelectProps(), ['value', 'onChange'])}
                        disabled={formikContext.isSubmitting}
                        className={styles.field}
                        onChange={async (value: ValueType<BrandOption, false>) =>
                            await setFallbackProjectImage(value?.image)
                        }
                        formatOptionLabel={(option: BrandOption, meta: FormatOptionLabelMeta<BrandOption, false>) =>
                            meta.context === 'menu' ? (
                                <BrandBadge
                                    name={option.label}
                                    teamName={option.subLabel}
                                    imageUrl={option.image ?? undefined}
                                />
                            ) : (
                                option.label
                            )
                        }
                    />

                    <Button
                        type="button"
                        appearance="ghost"
                        className={styles.createBrandButton}
                        onClick={() => setIsCreateBrandModalOpen(true)}
                        disabled={formikContext.isSubmitting}
                    >
                        Create artist
                    </Button>
                </div>
            </Form>

            <CreateBrandModal
                isOpen={isCreateBrandModalOpen}
                onClose={() => setIsCreateBrandModalOpen(false)}
                onCreated={handleBrandCreated}
            />
        </>
    );
};

export default ProjectForm;
